import { useState } from 'react';
import { api } from '../../lib/api';

interface ContentType {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  comingSoon?: boolean;
}

interface AddSiteWizardProps {
  onClose: () => void;
  onComplete: (siteId: string) => void;
}

type Step = 'url' | 'content-types' | 'authorize' | 'syncing' | 'complete';

const CONTENT_TYPES: { category: string; comingSoon?: boolean; types: ContentType[] }[] = [
  {
    category: 'WordPress Basics',
    types: [
      { id: 'posts', label: 'Posts', description: 'Blog posts and articles', enabled: true },
      { id: 'pages', label: 'Pages', description: 'Static pages', enabled: true },
      { id: 'media', label: 'Media', description: 'Images, videos, files', enabled: true },
      { id: 'users', label: 'Users', description: 'Site users and authors', enabled: false },
      { id: 'comments', label: 'Comments', description: 'Post comments', enabled: false },
      { id: 'taxonomies', label: 'Categories & Tags', description: 'Content organization', enabled: false },
    ],
  },
  {
    category: 'WooCommerce',
    comingSoon: true,
    types: [
      { id: 'products', label: 'Products', description: 'Store products', enabled: false, comingSoon: true },
      { id: 'orders', label: 'Orders', description: 'Customer orders', enabled: false, comingSoon: true },
      { id: 'coupons', label: 'Coupons', description: 'Discount coupons', enabled: false, comingSoon: true },
    ],
  },
  {
    category: 'Advanced',
    comingSoon: true,
    types: [
      { id: 'acf', label: 'ACF Fields', description: 'Advanced Custom Fields', enabled: false, comingSoon: true },
      { id: 'gravity_forms', label: 'Gravity Forms', description: 'Form entries', enabled: false, comingSoon: true },
    ],
  },
];

export default function AddSiteWizard({ onClose, onComplete }: AddSiteWizardProps) {
  const [step, setStep] = useState<Step>('url');
  const [siteUrl, setSiteUrl] = useState('');
  const [siteName, setSiteName] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['posts', 'pages', 'media']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteId, setSiteId] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, type: '' });

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Extract site name from URL if not provided
    if (!siteName) {
      try {
        const url = new URL(siteUrl);
        setSiteName(url.hostname);
      } catch {
        setError('Please enter a valid URL');
        return;
      }
    }

    setStep('content-types');
  };

  const toggleContentType = (typeId: string, comingSoon?: boolean) => {
    if (comingSoon) return;
    setSelectedTypes(prev =>
      prev.includes(typeId) ? prev.filter(t => t !== typeId) : [...prev, typeId]
    );
  };

  const handleContentTypesSubmit = async () => {
    if (selectedTypes.length === 0) {
      setError('Please select at least one content type');
      return;
    }
    setError('');
    setStep('authorize');
  };

  const handleAuthorize = async () => {
    setLoading(true);
    setError('');

    try {
      // First create the site in our system
      const response = await api.post<{ site: { id: string } }>('/api/v1/sites', {
        name: siteName,
        url: siteUrl,
        content_types: selectedTypes,
      });

      setSiteId(response.site.id);

      // Open WordPress authorization in new window
      const authUrl = `${siteUrl.replace(/\/$/, '')}/wp-admin/authorize-application.php?app_name=WP%20Cruddly&success_url=${encodeURIComponent(window.location.origin + '/auth-callback')}`;

      const authWindow = window.open(authUrl, 'wp-auth', 'width=600,height=700');

      // Listen for the callback
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'wp-auth-success') {
          window.removeEventListener('message', handleMessage);
          authWindow?.close();

          // Save the credentials to the site
          await api.patch(`/api/v1/sites/${response.site.id}`, {
            wp_username: event.data.username,
            wp_app_password: event.data.password,
          });

          // Start sync
          setStep('syncing');
          await startSync(response.site.id);
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was closed without completing auth
      const checkPopupClosed = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(checkPopupClosed);
          window.removeEventListener('message', handleMessage);
          // If we're still on authorize step, user cancelled
          if (step === 'authorize') {
            setLoading(false);
            setError('Authorization was cancelled. Click "Connect to WordPress" to try again.');
          }
        }
      }, 500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
      setLoading(false);
    }
  };

  const startSync = async (_siteId: string) => {
    // Simulate sync progress for now
    // TODO: Implement real sync with progress updates using _siteId
    const types = selectedTypes;
    let processed = 0;

    for (const type of types) {
      setSyncProgress({ current: processed, total: types.length, type });
      await new Promise(resolve => setTimeout(resolve, 1000));
      processed++;
    }

    setSyncProgress({ current: types.length, total: types.length, type: 'complete' });
    setStep('complete');
  };

  const renderStep = () => {
    switch (step) {
      case 'url':
        return (
          <form onSubmit={handleUrlSubmit} className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Connect Your WordPress Site</h2>
              <p className="text-sm text-gray-500 mt-1">Enter your WordPress site URL to get started</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="siteUrl" className="block text-sm font-medium text-gray-700">
                WordPress Site URL
              </label>
              <input
                id="siteUrl"
                type="url"
                required
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://yoursite.com"
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
              />
            </div>

            <div>
              <label htmlFor="siteName" className="block text-sm font-medium text-gray-700">
                Display Name (optional)
              </label>
              <input
                id="siteName"
                type="text"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                placeholder="My WordPress Site"
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Continue
              </button>
            </div>
          </form>
        );

      case 'content-types':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Select Content to Manage</h2>
              <p className="text-sm text-gray-500 mt-1">Choose what you want to sync and manage</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4 max-h-80 overflow-y-auto">
              {CONTENT_TYPES.map(category => (
                <div key={category.category} className={`border rounded-lg p-4 ${category.comingSoon ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">{category.category}</h3>
                    {category.comingSoon && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {category.types.map(type => (
                      <label
                        key={type.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          type.comingSoon
                            ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                            : selectedTypes.includes(type.id)
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(type.id)}
                          onChange={() => toggleContentType(type.id, type.comingSoon)}
                          disabled={type.comingSoon}
                          className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{type.label}</div>
                          <div className="text-xs text-gray-500">{type.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setStep('url')}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleContentTypesSubmit}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 'authorize':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Authorize Connection</h2>
              <p className="text-sm text-gray-500 mt-1">
                Click below to authorize WP Cruddly to access your WordPress site
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Read and edit {selectedTypes.join(', ')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No password sharing - uses Application Passwords</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Revoke access anytime from WordPress</span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setStep('content-types')}
                disabled={loading}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleAuthorize}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Connect to WordPress
                  </>
                )}
              </button>
            </div>
          </div>
        );

      case 'syncing':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Syncing Your Content</h2>
              <p className="text-sm text-gray-500 mt-1">
                {syncProgress.type === 'complete' ? 'Almost done...' : `Syncing ${syncProgress.type}...`}
              </p>
            </div>

            <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-full transition-all duration-300"
                style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
              />
            </div>

            <p className="text-center text-sm text-gray-500">
              {syncProgress.current} of {syncProgress.total} content types synced
            </p>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">All Set!</h2>
              <p className="text-sm text-gray-500 mt-1">
                Your WordPress site is connected and synced
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-green-900">{siteName}</div>
                  <div className="text-sm text-green-700">{siteUrl}</div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => siteId && onComplete(siteId)}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              View Your Content
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Progress indicator */}
          {step !== 'complete' && (
            <div className="flex items-center justify-center gap-2 mb-6">
              {['url', 'content-types', 'authorize', 'syncing'].map((s, i) => (
                <div
                  key={s}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    ['url', 'content-types', 'authorize', 'syncing'].indexOf(step) >= i
                      ? 'bg-blue-600'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}

          {renderStep()}
        </div>
      </div>
    </div>
  );
}
